/* eslint-disable no-param-reassign */
import PadletApi from 'surface/padlet_api'
import { captureFetchException } from 'bits/error_tracker'

export default {
  namespaced: true,
  state: {
    connectionsFetched: false,
    connectionEntities: {},
    pendingConnectionIds: [],
    postToConnectFromId: null,
    postToDisconnectFromId: null,
  },
  getters: {
    connections: (state, getters) => getters.connectionIds.map(id => state.connectionEntities[id]),
    connectionIds: state => {
      return Object.keys(state.connectionEntities)
    },
    xConnectionCancellation: state => !!state.postToConnectFromId,
    xDisconnectionCancellation: state => !!state.postToDisconnectFromId,
  },
  mutations: {
    FETCH_CONNECTIONS_SUCCESS(state, connections) {
      if (connections) {
        const newConnectionEntities = {}
        connections.forEach(r => {
          newConnectionEntities[r.id] = r
        })
        state.connectionEntities = newConnectionEntities // Trigger state change once
        state.pendingConnectionIds = []
      }
      state.connectionsFetched = true
    },
    ADD_PENDING_CONNECTION(state, pendingConnection) {
      const tempId = 'new_' + new Date().valueOf()
      state.connectionEntities = {
        ...state.connectionEntities,
        [tempId]: {
          ...pendingConnection,
          id: tempId,
        },
      }
      state.pendingConnectionIds.push(tempId)
    },
    ADD_CONNECTION(state, connection) {
      const pendingConnectionIdRemoved = state.pendingConnectionIds.some(tempId => {
        if (
          state.connectionEntities[tempId].from_wish_id === connection.from_wish_id &&
          state.connectionEntities[tempId].to_wish_id === connection.to_wish_id &&
          state.connectionEntities[tempId].label === connection.label
        ) {
          const newConnectionEntities = { ...state.connectionEntities }
          // delete pending connection
          delete newConnectionEntities[tempId]
          // put new connection in place of pending connection
          state.connectionEntities = {
            ...newConnectionEntities,
            [connection.id]: connection,
          }
          state.pendingConnectionIds = state.pendingConnectionIds.filter(i => i !== tempId)
          return true
        }
        return false
      })
      if (!pendingConnectionIdRemoved) {
        state.connectionEntities = {
          ...state.connectionEntities,
          [connection.id]: connection,
        }
      }
    },
    REMOVE_CONNECTION(state, id) {
      const newConnectionEntities = { ...state.connectionEntities }
      delete newConnectionEntities[id]
      state.connectionEntities = newConnectionEntities
    },
    REMOVE_CONNECTIONS(state, ids) {
      const newConnectionEntities = { ...state.connectionEntities }
      ids.forEach(id => delete newConnectionEntities[id])
      state.connectionEntities = newConnectionEntities
    },
    START_CONNECTION(state, postId) {
      state.postToConnectFromId = postId
    },
    START_DISCONNECTION(state, postId) {
      state.postToDisconnectFromId = postId
    },
    CANCEL_CONNECTION(state) {
      state.postToConnectFromId = null
    },
    CANCEL_DISCONNECTION(state) {
      state.postToDisconnectFromId = null
    },
  },
  actions: {
    /* ------------
     * CRUD
     * ------------ */
    fetchConnections({ dispatch, rootState }) {
      PadletApi.PostConnection.readAll({ wall: { id: rootState.wall.id } })
        .then(connections => {
          dispatch('fetchConnectionsSuccess', { connections })
        })
        .catch(e => {
          captureFetchException(e, { source: 'fetchConnections' })
        })
    },
    fetchConnectionsSuccess({ commit }, { connections }) {
      commit('FETCH_CONNECTIONS_SUCCESS', connections)
    },
    startConnection({ commit }, { postId }) {
      commit('START_CONNECTION', postId)
    },
    startDisconnection({ commit }, { postId }) {
      commit('START_DISCONNECTION', postId)
    },
    cancelConnection({ commit }) {
      commit('CANCEL_CONNECTION')
    },
    cancelDisconnection({ commit }) {
      commit('CANCEL_DISCONNECTION')
    },
    removeConnectionsForPost({ state, getters, commit }, { postId }) {
      const relatedConnections = getters.connectionIds.filter(id => {
        return (
          state.connectionEntities[id].from_wish_id === postId || state.connectionEntities[id].to_wish_id === postId
        )
      })
      commit('REMOVE_CONNECTIONS', relatedConnections)
    },
    completeConnection({ commit, state, dispatch }, { postToConnectToId, label }) {
      const newConnection = {
        from_wish_id: state.postToConnectFromId,
        to_wish_id: postToConnectToId,
        label: label,
      }
      commit('CANCEL_CONNECTION')
      commit('ADD_PENDING_CONNECTION', newConnection)
      PadletApi.PostConnection.create(newConnection).then(connection => {
        dispatch('completeConnectionSuccess', { connection })
      })
    },
    completeConnectionSuccess({ commit }, { connection }) {
      commit('ADD_CONNECTION', connection)
    },
    completeDisconnection({ commit, state }, { connectionId }) {
      const existingConnection = state.connectionEntities[connectionId]
      commit('CANCEL_DISCONNECTION')
      if (existingConnection) {
        commit('REMOVE_CONNECTION', existingConnection.id)
        PadletApi.PostConnection.delete(existingConnection)
      }
    },

    /* ------------
     * REMOTE
     * ------------ */
    newConnectionRemote({ commit }, connection) {
      commit('ADD_CONNECTION', connection)
    },
    deleteConnectionRemote({ commit }, connection) {
      commit('REMOVE_CONNECTION', connection.id)
    },
  },
}
